'use client';

// LES ÉCRANS DU TÉLÉPHONE — un par scène du voyage.
//
// Chacun rejoue un écran réel de l'application (bibliothèque, éditeur de grille, accès,
// interactions, carte, moment privé, notifications) avec ses vraies composantes visuelles.
// Tout est dessiné : aucune capture, aucune image à charger — donc net partout, thème-aware,
// et modifiable à la main. Les « photos » sont des décors CSS (cf. Scenery).

import {
  Bell,
  Check,
  Heart,
  Lock,
  MapPin,
  MessageCircle,
  Play,
  Plus,
  Search,
  Shield,
} from 'lucide-react';

import { useT } from '@/hooks/useT';
import { fontStyle } from '@/theme/typography';
import { Scenery, type SceneryKind } from './Scenery';

// Barre d'état + titre d'écran, communs à tous les écrans.
function ScreenChrome({
  title,
  right,
}: {
  title: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-4 pb-2 pt-7">
      <span
        className="text-text-primary"
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 19,
          fontWeight: 600,
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </span>
      {right}
    </div>
  );
}

// Petite carte de souvenir (décor + titre), réutilisée par plusieurs écrans.
function MemoryTile({
  kind,
  label,
  place,
  className = '',
  delay = 0,
}: {
  kind: SceneryKind;
  label: string;
  place?: string;
  className?: string;
  delay?: number;
}) {
  return (
    <div
      className={`rmbr-pop relative overflow-hidden rounded-lg shadow-[var(--rmbr-shadow-card)] ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <Scenery kind={kind} />
      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-0.5 p-2.5">
        <span
          className="text-on-media"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.15,
          }}
        >
          {label}
        </span>
        {place && (
          <span
            className="flex items-center gap-1 text-on-media-muted"
            style={{ ...fontStyle('caption'), fontSize: 9 }}
          >
            <MapPin className="size-2.5" />
            {place}
          </span>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ 1. La bibliothèque */
export function LibraryScreen() {
  const { t } = useT('landing');
  return (
    <>
      <ScreenChrome
        title={t('phone.library')}
        right={
          <span className="grid size-7 place-items-center rounded-full bg-fill-subtle text-text-secondary">
            <Search className="size-3.5" />
          </span>
        }
      />
      <div className="flex flex-col gap-2 px-3">
        <MemoryTile
          kind="lagoon"
          label={t('grid.title')}
          place={t('grid.place')}
          className="aspect-[3/2]"
          delay={60}
        />
        <MemoryTile
          kind="forest"
          label={t('phone.tile.forest')}
          place={t('phone.place.forest')}
          className="aspect-[3/2]"
          delay={200}
        />
        <MemoryTile
          kind="snow"
          label={t('phone.tile.snow')}
          className="aspect-[3/2]"
          delay={340}
        />
      </div>
    </>
  );
}

/* --------------------------------------------------------------- 2. L'éditeur de grille */
export function ComposeScreen() {
  const { t } = useT('landing');
  return (
    <>
      <ScreenChrome
        title={t('phone.compose')}
        right={
          <span
            className="rounded-full bg-action px-2.5 py-1 text-action-on"
            style={{ ...fontStyle('caption'), fontSize: 9 }}
          >
            {t('phone.save')}
          </span>
        }
      />
      {/* La grille se remplit case par case : c'est le geste central du produit. */}
      <div className="grid grid-cols-4 gap-1.5 px-3">
        <div
          className="rmbr-pop relative col-span-4 aspect-[2/1] overflow-hidden rounded-md"
          style={{ animationDelay: '60ms' }}
        >
          <Scenery kind="lagoon" />
        </div>
        <div
          className="rmbr-pop col-span-2 flex aspect-[2/1.1] items-center justify-center rounded-md border border-border bg-surface p-2"
          style={{ animationDelay: '190ms' }}
        >
          <span
            className="text-text-secondary"
            style={{
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontSize: 9,
              lineHeight: 1.3,
            }}
          >
            {t('phone.quote')}
          </span>
        </div>
        <div
          className="rmbr-pop relative col-span-2 aspect-[2/1.1] overflow-hidden rounded-md"
          style={{ animationDelay: '320ms' }}
        >
          <Scenery kind="dawn" sun={false} />
        </div>
        <div
          className="rmbr-pop relative col-span-2 aspect-square overflow-hidden rounded-md"
          style={{ animationDelay: '450ms' }}
        >
          <Scenery kind="desert" />
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid size-6 place-items-center rounded-full bg-on-media/90">
              <Play className="size-2.5 translate-x-px fill-current text-[color:var(--rmbr-media-backdrop)]" />
            </span>
          </span>
        </div>
        {/* La case vide volontaire, puis le bouton d'ajout : deux détails très « Remember ». */}
        <div
          className="rmbr-pop col-span-1 aspect-square rounded-md border border-dashed border-border"
          style={{ animationDelay: '560ms' }}
        />
        <div
          className="rmbr-pop col-span-1 grid aspect-square place-items-center rounded-md border border-dashed border-brand/60 bg-brand-muted/50 text-brand"
          style={{ animationDelay: '660ms' }}
        >
          <Plus className="size-4" />
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- 3. Le texte enrichi */
export function WriteScreen() {
  const { t } = useT('landing');
  return (
    <>
      <ScreenChrome title={t('phone.write')} />
      <div className="px-3">
        {/* Barre de mise en forme */}
        <div className="mb-2 flex items-center gap-1 rounded-md border border-border bg-surface p-1">
          {['B', 'I', 'U', 'S'].map((c, i) => (
            <span
              key={c}
              className={`rmbr-pop grid size-6 place-items-center rounded ${
                i === 0 ? 'bg-action text-action-on' : 'text-text-secondary'
              }`}
              style={{
                animationDelay: `${i * 90}ms`,
                fontWeight: i === 0 ? 700 : 500,
                fontSize: 11,
                fontStyle: i === 1 ? 'italic' : 'normal',
                textDecoration:
                  i === 2 ? 'underline' : i === 3 ? 'line-through' : 'none',
              }}
            >
              {c}
            </span>
          ))}
          <span className="ml-auto flex items-center gap-1">
            {['#C8613C', '#2E7F92', '#05C793'].map((c, i) => (
              <span
                key={c}
                className="rmbr-pop size-3.5 rounded-full"
                style={{ background: c, animationDelay: `${360 + i * 80}ms` }}
              />
            ))}
          </span>
        </div>

        <div className="rmbr-screen-in rounded-lg border border-border bg-surface p-3">
          <p
            className="text-text-primary"
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            {t('phone.story1')}{' '}
            <strong style={{ fontWeight: 700 }}>{t('phone.storyBold')}</strong>{' '}
            <em style={{ fontStyle: 'italic', color: 'var(--rmbr-action)' }}>
              {t('phone.storyItalic')}
            </em>{' '}
            {t('phone.story2')}
            <span className="rmbr-caret ml-0.5 inline-block h-[1em] w-[2px] translate-y-[0.15em] bg-brand" />
          </p>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------ 4. Les accès */
export function AccessScreen() {
  const { t } = useT('landing');
  // Les trois niveaux réels, du plus large au plus étroit. La maquette montrait « manager », rôle
  // disparu de ce chantier, et appelait des clés absentes du catalogue `landing` — sans fallbackNS,
  // i18next rendait la clé brute : « roles.manager.name » s'affichait sur la page d'accueil.
  const ROWS = [
    { key: 'owner', granted: 4 },
    { key: 'contributor', granted: 3 },
    { key: 'reader', granted: 1 },
  ] as const;

  return (
    <>
      <ScreenChrome
        title={t('phone.access')}
        right={
          <span className="grid size-7 place-items-center rounded-full bg-brand-muted text-brand">
            <Shield className="size-3.5" />
          </span>
        }
      />
      <div className="flex flex-col gap-2 px-3">
        {ROWS.map((row, i) => (
          <div
            key={row.key}
            className="rmbr-pop flex items-center gap-2.5 rounded-lg border border-border bg-surface p-2.5"
            style={{ animationDelay: `${i * 130}ms` }}
            title={t(`roles.${row.key}.desc`)}
          >
            <span className="size-7 shrink-0 rounded-full bg-action-muted" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span
                className="truncate text-text-primary"
                style={{ ...fontStyle('caption'), fontSize: 10 }}
              >
                {t(`phone.person.${i}`)}
              </span>
              <span
                className="truncate text-text-tertiary"
                style={{ ...fontStyle('caption'), fontSize: 9 }}
              >
                {t(`roles.${row.key}.name`)}
              </span>
            </span>
            {/* Jauge de niveau : autant de crans que le rôle en accorde. */}
            <span className="flex shrink-0 items-center gap-[2px]">
              {[0, 1, 2, 3, 4].map((n) => (
                <span
                  key={n}
                  className={`h-3 w-[2px] rounded-full ${n < row.granted ? 'bg-brand' : 'bg-border'}`}
                />
              ))}
            </span>
          </div>
        ))}

        <div
          className="rmbr-pop mt-1 flex items-center gap-2 rounded-lg border border-dashed border-brand/50 bg-brand-muted/40 p-2.5 text-brand"
          style={{ animationDelay: '420ms' }}
        >
          <Plus className="size-3.5" />
          <span style={{ ...fontStyle('caption'), fontSize: 10 }}>
            {t('phone.invite')}
          </span>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ 5. Les interactions */
export function SocialScreen() {
  const { t } = useT('landing');
  return (
    <>
      <ScreenChrome title={t('phone.social')} />
      <div className="px-3">
        <div className="rmbr-pop relative aspect-[3/2] overflow-hidden rounded-lg">
          <Scenery kind="city" />
          {/* Le cœur éclot par-dessus la photo. */}
          <span
            className="rmbr-pop absolute right-2.5 top-2.5 grid size-8 place-items-center rounded-full bg-black/35 backdrop-blur-md"
            style={{ animationDelay: '260ms' }}
          >
            <Heart className="size-4 fill-[color:var(--rmbr-danger)] text-[color:var(--rmbr-danger)]" />
          </span>
          <span
            className="rmbr-pop absolute bottom-2.5 left-2.5 flex items-center gap-1.5 rounded-full bg-black/35 px-2 py-1 text-on-media backdrop-blur-md"
            style={{
              ...fontStyle('caption'),
              fontSize: 9,
              animationDelay: '380ms',
            }}
          >
            <Heart className="size-2.5" /> 12
            <MessageCircle className="ml-1 size-2.5" /> 4
          </span>
        </div>

        {/* Fil de commentaires */}
        <div className="mt-2 flex flex-col gap-1.5">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="rmbr-pop flex items-start gap-2 rounded-lg border border-border bg-surface p-2"
              style={{ animationDelay: `${520 + i * 160}ms` }}
            >
              <span className="mt-0.5 size-5 shrink-0 rounded-full bg-brand-muted" />
              <span className="flex flex-col gap-0.5">
                <span
                  className="text-text-primary"
                  style={{ ...fontStyle('caption'), fontSize: 9.5 }}
                >
                  {t(`phone.person.${i}`)}
                </span>
                <span
                  className="text-text-secondary"
                  style={{
                    ...fontStyle('caption'),
                    fontSize: 9.5,
                    lineHeight: 1.35,
                  }}
                >
                  {t(`phone.comment.${i}`)}
                </span>
              </span>
            </div>
          ))}
        </div>

        {/* Notification qui arrive en bas de l'écran. */}
        <div
          className="rmbr-pop mt-2 flex items-center gap-2 rounded-lg border border-border bg-surface-elevated p-2 shadow-[var(--rmbr-shadow-card)]"
          style={{ animationDelay: '860ms' }}
        >
          <span className="grid size-6 shrink-0 place-items-center rounded-full bg-action-muted text-action">
            <Bell className="size-3" />
          </span>
          <span
            className="text-text-secondary"
            style={{ ...fontStyle('caption'), fontSize: 9.5 }}
          >
            {t('phone.notif')}
          </span>
        </div>
      </div>
    </>
  );
}

/* --------------------------------------------------------------------------- 6. La carte */
export function MapScreen() {
  const { t } = useT('landing');
  const PINS = [
    { left: '26%', top: '30%', delay: 0 },
    { left: '58%', top: '22%', delay: 700 },
    { left: '44%', top: '54%', delay: 1400 },
    { left: '72%', top: '62%', delay: 2100 },
  ];

  return (
    <>
      <ScreenChrome title={t('phone.map')} />
      <div className="px-3">
        <div className="relative aspect-[3/4] overflow-hidden rounded-lg border border-border bg-fill-subtle">
          {/* Fond de carte stylisé : mer, terre, routes. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(160deg, #DCE9F0 0%, #E9EFE4 55%, #F2EDE2 100%)',
            }}
          />
          <svg
            className="absolute inset-0 size-full"
            viewBox="0 0 100 133"
            preserveAspectRatio="none"
          >
            <path d="M0 42 Q26 30 48 44 T100 38 L100 0 L0 0 Z" fill="#CFE0EA" />
            <path
              d="M0 96 Q30 84 54 98 T100 92 L100 133 L0 133 Z"
              fill="#DFE9D6"
            />
            <path
              d="M10 120 Q34 76 52 60 T92 20"
              stroke="#B9C6CE"
              strokeWidth="1.2"
              fill="none"
              strokeDasharray="3 2.5"
            />
          </svg>

          {PINS.map((p, i) => (
            <span
              key={i}
              className="absolute"
              style={{ left: p.left, top: p.top }}
            >
              <span
                className="rmbr-ping absolute -inset-3 rounded-full bg-brand/40"
                style={{ animationDelay: `${p.delay}ms` }}
              />
              <span
                className="rmbr-pop relative grid size-5 place-items-center rounded-full bg-action text-action-on shadow-[var(--rmbr-shadow-md)]"
                style={{ animationDelay: `${i * 140}ms` }}
              >
                <MapPin className="size-2.5" />
              </span>
            </span>
          ))}

          {/* Carte flottante du souvenir sélectionné. */}
          <div
            className="rmbr-pop absolute inset-x-2 bottom-2 flex items-center gap-2 rounded-lg border border-border bg-surface/95 p-2 backdrop-blur"
            style={{ animationDelay: '620ms' }}
          >
            <span className="relative size-9 shrink-0 overflow-hidden rounded-md">
              <Scenery kind="lagoon" sun={false} />
            </span>
            <span className="flex min-w-0 flex-col">
              <span
                className="truncate text-text-primary"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {t('grid.title')}
              </span>
              <span
                className="truncate text-text-tertiary"
                style={{ ...fontStyle('caption'), fontSize: 9 }}
              >
                {t('grid.place')}
              </span>
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- 7. Le moment privé */
export function PrivateScreen() {
  const { t } = useT('landing');
  return (
    <>
      <ScreenChrome
        title={t('phone.private')}
        right={
          <span className="grid size-7 place-items-center rounded-full bg-fill-subtle text-locked">
            <Lock className="size-3.5" />
          </span>
        }
      />
      <div className="grid grid-cols-2 gap-1.5 px-3">
        <div
          className="rmbr-pop relative aspect-square overflow-hidden rounded-md"
          style={{ animationDelay: '40ms' }}
        >
          <Scenery kind="night" />
        </div>
        {/* Le moment privé garde sa place, mais reste fermé pour les autres. */}
        <div
          className="rmbr-pop grid aspect-square place-items-center rounded-md border border-dashed border-locked bg-fill-subtle"
          style={{ animationDelay: '160ms' }}
        >
          <span className="flex flex-col items-center gap-1">
            <Lock className="size-4 text-locked" />
            <span
              className="text-text-tertiary"
              style={{ ...fontStyle('caption'), fontSize: 8.5 }}
            >
              {t('grid.private')}
            </span>
          </span>
        </div>
        <div
          className="rmbr-pop relative col-span-2 aspect-[2/1] overflow-hidden rounded-md"
          style={{ animationDelay: '280ms' }}
        >
          <Scenery kind="snow" />
        </div>

        {/* Bandeau de réassurance : ce que le serveur garantit. */}
        <div
          className="rmbr-pop col-span-2 mt-1 flex items-center gap-2 rounded-lg border border-border bg-surface p-2"
          style={{ animationDelay: '420ms' }}
        >
          <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand-muted text-brand">
            <Check className="size-3" />
          </span>
          <span
            className="text-text-secondary"
            style={{ ...fontStyle('caption'), fontSize: 9.5, lineHeight: 1.35 }}
          >
            {t('phone.exif')}
          </span>
        </div>
      </div>
    </>
  );
}
